import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import Dialog from "@/components/Dialog";
import { ApolloError, useMutation } from "@apollo/client";
import { MARK_ORDER_UNCOLLECTIBLE_MUTATION, COMMERCE_AUDIT_LOG_QUERY } from "../queries";
import { OrderType } from "../types";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { showSnackbar } from "@/lib/snackbarUtils";
import { NotePriorityValues } from "@/types";
import type { NotePriority } from "@/types";
import NoteInputFields from "@/features/global/components/NotesTable/NoteInputFields";
import { buildNoteInput } from "@/features/global/components/NotesTable/noteHelpers";

interface MarkUncollectibleFormValues {
  noteContent: string;
  notePriority: NotePriority;
}

const ACKNOWLEDGE_UNCOLLECTIBLE_LABEL =
  "I understand that marking this order uncollectible cannot be undone and the payor will no longer be able to pay it.";
const UNCOLLECTIBLE_MUTATION_ERROR = "Unable to mark this order uncollectible. Please try again.";

function uncollectibleMutationErrorMessage(error: unknown): string {
  if (error instanceof ApolloError) {
    return error.graphQLErrors[0]?.message || error.message || UNCOLLECTIBLE_MUTATION_ERROR;
  }

  return UNCOLLECTIBLE_MUTATION_ERROR;
}

export const MarkUncollectibleModal = ({
  onDismiss,
  order,
  refetch
}: {
  onDismiss: () => void;
  order: OrderType;
  refetch?: () => void;
}) => {
  const [markUncollectibleMutation, { loading }] = useMutation(MARK_ORDER_UNCOLLECTIBLE_MUTATION, {
    refetchQueries: [COMMERCE_AUDIT_LOG_QUERY],
    awaitRefetchQueries: false
  });
  const headline = "Mark Order Uncollectible";
  const { control, getValues } = useForm<MarkUncollectibleFormValues>({
    defaultValues: { noteContent: "", notePriority: NotePriorityValues.NONE }
  });
  const [errorContent, setErrorContent] = useState<string>("");
  const [acknowledged, setAcknowledged] = useState(false);

  async function markCommerceOrderUncollectible() {
    if (!acknowledged) {
      return;
    }

    const noteContent = getValues("noteContent");
    const notePriority = getValues("notePriority");

    try {
      const { data } = await markUncollectibleMutation({
        variables: {
          input: {
            commerceOrderId: order.id,
            note: buildNoteInput(noteContent, notePriority, headline)
          }
        }
      });

      if (data?.markCommerceOrderUncollectible?.success) {
        refetch && refetch();
        showSnackbar("Order marked uncollectible");
        onDismiss();
        return;
      }

      setErrorContent(data?.markCommerceOrderUncollectible?.message || UNCOLLECTIBLE_MUTATION_ERROR);
    } catch (error) {
      setErrorContent(uncollectibleMutationErrorMessage(error));
    }
  }

  const actions = (
    <>
      <Button variant="text" onClick={onDismiss}>
        Go Back
      </Button>

      <Button
        onClick={markCommerceOrderUncollectible}
        disabled={loading || !acknowledged}
        testId="mark-uncollectible-confirm"
      >
        Mark Uncollectible
      </Button>
    </>
  );

  return (
    <Dialog
      onClosed={onDismiss}
      open
      actions={actions}
      headline={headline}
      className="h-fit max-h-[90vh]"
      hasFormElements
    >
      {errorContent ? (
        <div className="text-red-500 font-bold text-center mb-4" role="alert">
          {errorContent}
        </div>
      ) : null}
      Are you sure you want to mark this dues order uncollectible? The payor will no longer be able to pay this order.
      Collected payments stay on the order and can still be refunded. This cannot be undone.
      <div className="mt-6">
        <Checkbox
          name="acknowledge-uncollectible"
          label={ACKNOWLEDGE_UNCOLLECTIBLE_LABEL}
          checked={acknowledged}
          onChange={setAcknowledged}
        />
      </div>
      <div className="mt-6">
        <NoteInputFields
          control={control}
          contentName="noteContent"
          priorityName="notePriority"
          priorityDropdownPlacement="above"
        />
      </div>
    </Dialog>
  );
};
